import clsx from 'clsx';
import React from 'react';
import { UiFlex, UiGrid, UiBox } from '@open-stack/ui';
import { ButtonIncrementDecrement } from '../../button/increment-decrement/ButtonIncrementDecrement';
import { MenuProductCardOrderProduct } from './order-product/MenuProductCardOrderProduct';
import { useOrderProductOverlay } from '../../../hooks/order-product-overlay';
import { MenuProductCardIcon } from '../../menu/product/card/icon/MenuProductCardIcon';
import { MenuProduct } from '../../../XV2';
import { MenuProductPriceLabel } from '../price-label/MenuProductPriceLabel';
import { MenuProductAllergen } from '../allergen/MenuProductAllergen';
import { useToggle } from '../../../hooks/toggle';
import { MenuProductCardDetails } from './details/MenuProductCardDetails';
import { XText, XTextType } from '../../x/text/XText';
import { useActiveOrder } from '../../../hooks/active-order';
import './MenuProductCard.scss';

interface Props {
    menuProduct: MenuProduct;
}

export function MenuProductCard({ menuProduct }: Props) {
    const { productId, product } = menuProduct;
    const { name, description, icon, isOutOfStock } = product;
    const { setProduct } = useOrderProductOverlay();
    const { addProduct, removeProduct, getOrderProductsByProductId } = useActiveOrder();
    const orderProducts = getOrderProductsByProductId(productId);
    const count = orderProducts.length;
    const isDialogRequired = product.variantGroup || product.productModifierGroups.length > 0;
    const hasProducts = count > 0;
    const [isDetailsVisible, toggleIsDetailsVisible] = useToggle();

    const isOrderProductsVisible =
        hasProducts && (product.isCommentAllowed || product.variantGroup || product.productModifierGroups.length > 0);

    function increment(amount) {
        if (isOutOfStock) {
            return;
        }

        if (isDialogRequired) {
            setProduct(menuProduct);
        } else {
            addProduct(menuProduct, amount);
        }
    }

    function decrement(amount) {
        removeProduct(menuProduct, amount);
    }

    const isAlcohol = menuProduct.product.alcoholPercent && menuProduct.product.alcoholPercent > 0;
    const isProductCountVisible = !isOutOfStock && hasProducts;

    const allergenCount = menuProduct.product.allergens?.length || 0;
    const hasAllergens = allergenCount > 0;

    return (
        <UiGrid padding={1} gap={1} className={clsx('MenuProductCard', { active: hasProducts, isOutOfStock })}>
            {isOutOfStock && (
                <UiFlex className="out-of-stock" justifyContent="center" fullWidth fullHeight padding={2}>
                    <XText align="center">
                        {name}
                        <br />
                        is out of stock
                    </XText>
                </UiFlex>
            )}

            <div onClick={() => increment(1)} className="card">
                {(icon || isAlcohol) && (
                    <UiBox
                        onClick={(event) => {
                            if (description) {
                                event.stopPropagation();
                                toggleIsDetailsVisible();
                            }
                        }}
                        position="relative"
                        paddingRight={2}>
                        {icon && <MenuProductCardIcon icon={icon} />}

                        {isAlcohol && (
                            <div className="alcohol-percent">{Number(menuProduct.product.alcoholPercent)}%</div>
                        )}
                    </UiBox>
                )}

                <UiFlex gap={2} justifyContent="space-between" fullWidth>
                    <UiGrid gap={0} fullWidth>
                        <XText type={XTextType.TITLE}>{name}</XText>

                        {description && (
                            <XText type={XTextType.PRODUCT_DESCRIPTION} className={clsx('description')}>
                                {description}
                            </XText>
                        )}

                        <XText>
                            <MenuProductPriceLabel menuProduct={menuProduct} />
                        </XText>
                    </UiGrid>

                    {hasAllergens && (
                        <div
                            className={clsx('allergen-container', {
                                hasProducts,
                                singleAllergen: allergenCount === 1,
                            })}>
                            <MenuProductAllergen menuProduct={menuProduct} />
                        </div>
                    )}

                    {isProductCountVisible && (
                        <div className="options-container">
                            <ButtonIncrementDecrement count={count} onIncrement={increment} onDecrement={decrement} />
                        </div>
                    )}
                </UiFlex>
            </div>

            {isOrderProductsVisible && (
                <UiGrid gap={1} className="container-extra">
                    {orderProducts.map((orderProduct, index) => (
                        <MenuProductCardOrderProduct
                            menuProduct={menuProduct}
                            orderProductId={orderProduct.id}
                            product={product}
                            position={index + 1}
                            key={orderProduct.id}
                        />
                    ))}
                </UiGrid>
            )}

            {isDetailsVisible && <MenuProductCardDetails menuProduct={menuProduct} onClose={toggleIsDetailsVisible} />}
        </UiGrid>
    );
}
